import { useAccount, useMsal } from '@azure/msal-react';
import { BakerUserPermissionsGuard, useBakerUserPermissions } from '@bakerweb/auth';
import { BakerDashboardLayout, LoadingScreen, LogoOnlyLayout } from '@bakerweb/layouts';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useRoutes } from 'react-router-dom';

// Supplies the Entra account + sign-out to the shared layout, whose
// AccountPopover otherwise defaults to the Cognito/Amplify session (still used by
// the client app).
function BakerLayout() {
  const { instance, accounts } = useMsal();
  const activeAccount = useAccount(accounts[0] || {});
  const signOut = () => {
    sessionStorage.setItem('baker-just-signed-out', 'true');
    // Pass the active account so Entra ends that specific session and skips the
    // "which account do you want to sign out of?" picker.
    instance.logoutRedirect({ account: instance.getActiveAccount() ?? activeAccount });
  };
  return <BakerDashboardLayout account={{ email: activeAccount?.username ?? null, onSignOut: signOut }} />;
}

const Loadable = (Component: any) => {
  const LoadableComponent = (props: any) => {
    const { pathname } = useLocation();
    const isDashboard = pathname.includes('/client-dashboard');

    return (
      <Suspense
        fallback={
          <LoadingScreen
            sx={{
              ...(!isDashboard && {
                top: 0,
                left: 0,
                width: 1,
                zIndex: 9999,
                position: 'fixed'
              })
            }}
          />
        }
      >
        <Component {...props} />
      </Suspense>
    );
  };
  return LoadableComponent;
};

export default function Router() {
  const location = useLocation();
  const {
    refetch,
    isAdmin,
    isInventory,
    isLoans,
    isLseg,
    isDailyPricing,
    isReports,
    isPortfolios,
    isCashFlows,
    isSwapIQ,
    isHoustonTrust,
    isEmpyreanImport,
    isYieldBookMonitor
  } = useBakerUserPermissions();

  // Fetches latest permissions on every location.pathname change
  useEffect(() => {
    refetch();
  }, [location.pathname]);

  return useRoutes([
    {
      path: '/',
      element: <Navigate to="/dashboard" replace />
    },
    {
      path: 'dashboard',
      element: <BakerLayout />,
      children: [
        {
          index: true,
          element: <DashboardHome />
        },
        {
          path: 'lseg',
          children: [
            {
              path: 'on-demand',
              element: (
                <BakerUserPermissionsGuard isAllowed={isLseg || isAdmin}>
                  <LSEGOnDemand />
                </BakerUserPermissionsGuard>
              )
            },
            {
              path: 'daily-pricing-bonds',
              element: (
                <BakerUserPermissionsGuard isAllowed={isDailyPricing || isAdmin}>
                  <DailyPricingBonds />
                </BakerUserPermissionsGuard>
              )
            }
          ]
        },
        {
          path: 'cash-flows',
          element: (
            <BakerUserPermissionsGuard isAllowed={isCashFlows || isAdmin}>
              <CashFlowsLayout />
            </BakerUserPermissionsGuard>
          ),
          children: [
            {
              index: true,
              element: <CashFlowsPage />
            }
          ]
        },
        {
          path: 'bond-swap',
          element: (
            <BakerUserPermissionsGuard isAllowed={isSwapIQ || isAdmin}>
              <BondSwapPage />
            </BakerUserPermissionsGuard>
          )
        },
        {
          path: 'houston-trust',
          element: (
            <BakerUserPermissionsGuard isAllowed={isHoustonTrust || isAdmin}>
              <HoustonTrustPage />
            </BakerUserPermissionsGuard>
          )
        },
        {
          // `/*`: Emport's URL is the open client and the step (`/empyrean-import/HRVB/input-file`), and the
          // lib reads everything below the mount from the splat — Emport Redesign B9
          path: 'empyrean-import/*',
          element: (
            <BakerUserPermissionsGuard isAllowed={isEmpyreanImport || isAdmin}>
              <EmpyreanImportPage />
            </BakerUserPermissionsGuard>
          )
        },
        {
          path: 'yield-book-monitor',
          element: (
            <BakerUserPermissionsGuard isAllowed={isYieldBookMonitor || isAdmin}>
              <YieldBookMonitorPage />
            </BakerUserPermissionsGuard>
          )
        },
        {
          path: 'search',
          children: [
            {
              path: 'general',
              element: (
                <BakerUserPermissionsGuard isAllowed={isPortfolios || isAdmin}>
                  <GeneralSearchPage />
                </BakerUserPermissionsGuard>
              )
            },
            {
              path: 'muni',
              element: (
                <BakerUserPermissionsGuard isAllowed={isPortfolios || isAdmin}>
                  <MuniSearchPage />
                </BakerUserPermissionsGuard>
              )
            }
          ]
        },
        {
          path: 'inventory',
          element: (
            <BakerUserPermissionsGuard isAllowed={isInventory || isAdmin}>
              <InventoryPage />
            </BakerUserPermissionsGuard>
          )
        },
        {
          path: 'loans',
          element: (
            <BakerUserPermissionsGuard isAllowed={isLoans || isAdmin}>
              <Outlet />
            </BakerUserPermissionsGuard>
          ),
          children: [
            {
              path: 'autos',
              element: <AutosPage />
            },
            {
              path: 'consumers',
              element: <ConsumersPage />
            },
            {
              path: 'cre',
              element: <CresPage />
            },
            {
              path: 'general',
              element: <GeneralPage />
            },
            {
              path: 'residential',
              element: <ResidentialPage />
            }
          ]
        },
        {
          path: 'reports',
          element: (
            <BakerUserPermissionsGuard isAllowed={isReports || isAdmin}>
              <FinancialReportsPage />
            </BakerUserPermissionsGuard>
          )
        },
        {
          path: 'admin',
          element: (
            <BakerUserPermissionsGuard isAllowed={isAdmin}>
              <Outlet />
            </BakerUserPermissionsGuard>
          ),
          children: [
            {
              path: 'baker/users',
              element: <BakerAdminPage />
            },
            {
              path: 'baker/users/:userId',
              element: <BakerUserEditPage />
            },
            {
              path: 'client-portal/users',
              element: <ClientAdminPage />
            },
            {
              path: 'client-portal/portfolios',
              element: <ClientPortfolioAdminPage />
            },
            {
              path: 'client-portal/portfolios/:portfolioCode',
              element: <ClientPortfolioDetailsPage />
            },
            {
              path: 'client-portal/users/:userId',
              element: <ClientUserEditPage />
            },
            {
              path: 'client-portal/users/new',
              element: <ClientNewUserPage />
            },
            {
              path: 'trusts/users',
              element: <TrustsAdminPage />
            },
            {
              path: 'trusts/users/:userId',
              element: <TrustsUserEditPage />
            },
            {
              path: 'trusts/users/new',
              element: <TrustsNewUserPage />
            },
            {
              path: 'yield-book/json-payloads',
              element: <JsonPayloadsPage />
            },
            {
              path: 'yield-book/json-payloads/:recordId',
              element: <JsonPayloadDetailPage />
            }
          ]
        }
      ]
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

const DashboardHome = Loadable(lazy(() => import('../pages/dashboard/DashboardHome')));
const LSEGOnDemand = Loadable(lazy(() => import('../pages/lseg-pricing/LSEGOnDemand')));
const DailyPricingBonds = Loadable(lazy(() => import('../pages/lseg-pricing/DailyPricingBonds')));
const AutosPage = Loadable(lazy(() => import('../pages/loans/AutosPage')));
const ConsumersPage = Loadable(lazy(() => import('../pages/loans/ConsumersPage')));
const ResidentialPage = Loadable(lazy(() => import('../pages/loans/ResidentialPage')));
const CresPage = Loadable(lazy(() => import('../pages/loans/CresPage')));
const GeneralPage = Loadable(lazy(() => import('../pages/loans/GeneralPage')));

const HoustonTrustPage = Loadable(lazy(() => import('@bakerweb/houston-trust')));
const YieldBookMonitorPage = Loadable(lazy(() => import('@bakerweb/yield-book-monitor')));

const GeneralSearchPage = Loadable(lazy(() => import('../pages/search-bonds/GeneralSearchPage')));
const MuniSearchPage = Loadable(lazy(() => import('../pages/search-bonds/MuniSearchPage')));

const InventoryPage = Loadable(lazy(() => import('../pages/inventory/InventoryPage')));

const BakerAdminPage = Loadable(lazy(() => import('../pages/admin/baker/BakerUsersPage')));
const BakerUserEditPage = Loadable(lazy(() => import('../pages/admin/baker/UserEditPage')));

const ClientAdminPage = Loadable(lazy(() => import('../pages/admin/client-portal/ClientUsersPage')));
const ClientPortfolioAdminPage = Loadable(lazy(() => import('../pages/admin/client-portal/ClientPortfoliosPage')));
const ClientPortfolioDetailsPage = Loadable(
  lazy(() => import('../pages/admin/client-portal/ClientPortfolioDetailsPage'))
);
const ClientNewUserPage = Loadable(lazy(() => import('../pages/admin/client-portal/NewUserPage')));
const ClientUserEditPage = Loadable(lazy(() => import('../pages/admin/client-portal/UserEditPage')));

const TrustsAdminPage = Loadable(lazy(() => import('../pages/admin/trusts/TrustsUsersPage')));
const TrustsNewUserPage = Loadable(lazy(() => import('../pages/admin/trusts/NewTrustsUserPage')));
const TrustsUserEditPage = Loadable(lazy(() => import('../pages/admin/trusts/TrustsUserEditPage')));

const FinancialReportsPage = Loadable(lazy(() => import('../pages/reports/FinancialReportsPage')));
const CashFlowsLayout = Loadable(lazy(() => import('../pages/cash-flows/CashFlowsLayout')));
const CashFlowsPage = Loadable(lazy(() => import('../pages/CashFlowsPage')));
const JsonPayloadsPage = Loadable(lazy(() => import('../pages/cash-flows/JsonPayloadsPage')));
const JsonPayloadDetailPage = Loadable(lazy(() => import('../pages/cash-flows/JsonPayloadDetailPage')));
const BondSwapPage = Loadable(lazy(() => import('../pages/bond-swap/BondSwapPage')));
const EmpyreanImportPage = Loadable(lazy(() => import('../pages/empyrean-import/EmpyreanImportPage')));

const NotFound = Loadable(lazy(() => import('../pages/Page404')));
